import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["actionCardPropsById", "onSelectAction", "canvasScrollElementRef", "flow", "onMouseEnterNode", "onMouseLeaveNode", "newActionNode"];
import { useCallback, useMemo } from 'react';
import FlowCanvasBase from 'automation-ui-canvas/containers/FlowCanvasBase';
import pannableDecorator from 'automation-ui-canvas/decorators/PannableDecorator';
import { getActionOutputsForTargetActionById } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import FlowCanvasNode from 'automation-ui-canvas/components/canvas/FlowCanvasNode';
import { useHistory, withRouter } from 'react-router-dom';
import * as SpecialActionIds from 'automation-ui-flow-core/canvas/SpecialActionIds';
import { getBranchIndex, getPreviousActionId, isGoToActionId } from 'automation-ui-action-features/records/recordsUtils';
import { OZ_DARK } from 'HubStyleTokens/colors';
import { compose } from 'redux';
import LazyLangLoaderDecorator from 'automation-ui-i18n/LazyLangLoaderDecorator';
import styled from 'styled-components';
import { useActionOutputs } from 'automation-ui-action-features/action-outputs/actionOutputHooks';
import { wireAuthConsumer } from 'automation-ui-core/context/AuthContext';
import actionFeaturesTranslations from 'i2l?query=sporks!automation-ui-canvas/lang/action-features/en.lyaml';
import ZoomableDecorator from 'automation-ui-canvas/decorators/ZoomableDecorator';
import { getZoomLevel } from 'automation-ui-canvas/selectors/localStorageSelectors';
import { useSelector } from 'react-redux';
import EndMarker from 'automation-ui-canvas/components/common/EndMarker';
import BranchDescription from 'automation-ui-canvas/components/canvas/BranchDescription';
import BranchName from 'automation-ui-canvas/components/canvas/BranchName';
import BranchLabel from 'automation-ui-canvas/components/canvas/BranchLabel';
import ActionThemes from 'automation-ui-flow-core/canvas/ActionThemes';
import { isEdgeOfBranchAction } from 'automation-ui-canvas/components/canvas/next-action-recommender/utils/isAllowedRecommendationEdgeForBranchAction';
import { Popover } from 'foundations-components/transitional/components/Popover';
import FormattedMessage from 'I18n/components/FormattedMessage';
import GoToActionCardWrapper from 'automation-ui-canvas/action-features/go-to/GoToActionCardWrapper';
import { DETAIL_MIN_ZOOM_LEVEL } from 'automation-ui-flow-core/canvas/graphConstants';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledCanvas = styled.div.withConfig({
  displayName: "FlowViewSimpleCanvas__StyledCanvas",
  componentId: "sc-1jvlm39-0"
})(["height:100%;"]);
function getComposedCanvas() {
  return pannableDecorator(
  // @ts-expect-error ZoomableDecorator expects StyledCanvas to have its required props, but it's just a div
  // and the required props aren't wired until ComposedCanvas is used in FlowCanvasBase. Not sure if real error or not
  ZoomableDecorator({
    WrappedComponent: StyledCanvas
  }));
}
const FlowViewSimpleCanvas = _ref => {
  let {
      actionCardPropsById,
      // eslint-disable-next-line react/prop-types
      onSelectAction,
      canvasScrollElementRef,
      flow,
      onMouseEnterNode,
      onMouseLeaveNode,
      newActionNode
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const ComposedCanvas = useMemo(() => getComposedCanvas(), []);
  const history = useHistory();
  const zoomLevel = useSelector(getZoomLevel(flow.flowId));
  const goToEditNodeId = useMemo(() => {
    if (!actionCardPropsById) return null;
    let selectedGoToAction = null;
    actionCardPropsById.forEach((props, id) => {
      if (isGoToActionId(id) && (props === null || props === void 0 ? void 0 : props.borderStyle) === 'selected') {
        selectedGoToAction = id;
      }
    });
    return selectedGoToAction;
  }, [actionCardPropsById]);
  const {
    instanceOutputsByActionId,
    allOutputsByActionTypeId
  } = useActionOutputs(flow);
  const highlightedActionIds = actionCardPropsById && new Set(actionCardPropsById.filter(cardProps => !!cardProps.borderStyle).map((_, actionId) => actionId).toArray());
  const handleGetActionOutputsForTargetActionById = useCallback((targetActionId, isNew) => {
    return getActionOutputsForTargetActionById({
      flow: flow,
      allOutputsByActionTypeId,
      instanceOutputsByActionId,
      targetActionId,
      isNewAction: isNew
    });
  }, [allOutputsByActionTypeId, flow, instanceOutputsByActionId]);
  const onSelectNode = useCallback(nodeId => {
    if (onSelectAction) {
      onSelectAction(nodeId);
    }
  }, [onSelectAction]);
  const handleMouseEnterNode = useCallback(id => {
    if (onMouseEnterNode) {
      onMouseEnterNode(id);
    }
  }, [onMouseEnterNode]);
  const handleMouseLeaveNode = useCallback(id => {
    if (onMouseLeaveNode) {
      onMouseLeaveNode(id);
    }
  }, [onMouseLeaveNode]);
  const onRenderCanvasNode = useCallback(({
    id,
    width,
    height
  }) => {
    var _searchParams$get$spl, _searchParams$get;
    let clickable = true;
    if (!flow) return null;
    if (id === SpecialActionIds.ENROLLMENT || id === SpecialActionIds.GO_TO) {
      clickable = false;
    }
    const searchParams = new URLSearchParams(history.location.search);
    const currentFilteredActions = (_searchParams$get$spl = (_searchParams$get = searchParams.get('actions')) === null || _searchParams$get === void 0 ? void 0 : _searchParams$get.split(',')) !== null && _searchParams$get$spl !== void 0 ? _searchParams$get$spl : [];
    const isSelected = currentFilteredActions.includes(id.toString());
    const actionCardProps = actionCardPropsById === null || actionCardPropsById === void 0 ? void 0 : actionCardPropsById.get(id);
    const overrideAccentColor = (actionCardProps === null || actionCardProps === void 0 ? void 0 : actionCardProps.cardTheme) && ActionThemes[actionCardProps === null || actionCardProps === void 0 ? void 0 : actionCardProps.cardTheme].accentColor;
    const hasPopover = !!(actionCardProps !== null && actionCardProps !== void 0 && actionCardProps.renderConfirmationPopoverContent);
    if (id === SpecialActionIds.NEW_ACTION && newActionNode) {
      return newActionNode({
        id,
        width,
        height,
        zoomLevel
      });
    }
    if (isGoToActionId(id)) {
      return /*#__PURE__*/_jsx(GoToActionCardWrapper, {
        flow: flow,
        isSelected: false,
        isHighlighted: !!(actionCardProps !== null && actionCardProps !== void 0 && actionCardProps.borderStyle) || id === SpecialActionIds.NEW_ACTION,
        isDisabled: actionCardProps === null || actionCardProps === void 0 ? void 0 : actionCardProps.disabled,
        disabledDescription: actionCardProps !== null && actionCardProps !== void 0 && actionCardProps.disabledDescription ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: actionCardProps === null || actionCardProps === void 0 ? void 0 : actionCardProps.disabledDescription
        }) : undefined,
        width: width,
        height: height,
        actionId: id,
        layout: zoomLevel < DETAIL_MIN_ZOOM_LEVEL ? 'zoomed-out' : 'default'
      });
    }
    const flowCanvasNodeContent = /*#__PURE__*/_jsx(FlowCanvasNode, {
      isDisabled: actionCardProps === null || actionCardProps === void 0 ? void 0 : actionCardProps.disabled,
      disabledDescription: actionCardProps !== null && actionCardProps !== void 0 && actionCardProps.disabledDescription ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: actionCardProps === null || actionCardProps === void 0 ? void 0 : actionCardProps.disabledDescription
      }) : undefined,
      id: id,
      width: width,
      height: height,
      isSelected: isSelected,
      clickable: clickable,
      flow: flow,
      readOnly: true,
      showIcon: true,
      onSelectNode: onSelectNode,
      isHighlighted: !!(actionCardProps !== null && actionCardProps !== void 0 && actionCardProps.borderStyle),
      getActionOutputsForTargetActionById: handleGetActionOutputsForTargetActionById,
      borderStyle: actionCardProps === null || actionCardProps === void 0 ? void 0 : actionCardProps.borderStyle,
      bannerStyle: actionCardProps === null || actionCardProps === void 0 ? void 0 : actionCardProps.bannerStyle,
      bannerText: actionCardProps === null || actionCardProps === void 0 ? void 0 : actionCardProps.bannerText,
      zoomLevel: zoomLevel,
      overrideAccentColor: overrideAccentColor
    });
    let contentsToRender = flowCanvasNodeContent;
    if (hasPopover) {
      contentsToRender = /*#__PURE__*/_jsx(Popover, {
        open: hasPopover,
        closeOnTargetLeave: true,
        closeOnOutsideClick: true,
        Content: actionCardProps === null || actionCardProps === void 0 ? void 0 : actionCardProps.renderConfirmationPopoverContent,
        children: flowCanvasNodeContent
      });
    }
    return /*#__PURE__*/_jsx("div", {
      onMouseEnter: () => {
        handleMouseEnterNode(id);
      },
      onMouseLeave: () => {
        handleMouseLeaveNode(id);
      },
      children: contentsToRender
    });
  }, [flow, handleGetActionOutputsForTargetActionById, history.location.search, actionCardPropsById, onSelectNode, handleMouseEnterNode, handleMouseLeaveNode, zoomLevel, newActionNode]);
  const getIsBranchEdgeHighlighted = useCallback(edgeId => {
    var _actionCardPropsById$;
    const prevActionId = getPreviousActionId(edgeId);
    const branchIndex = getBranchIndex(edgeId);
    return (actionCardPropsById === null || actionCardPropsById === void 0 || (_actionCardPropsById$ = actionCardPropsById.get(prevActionId)) === null || _actionCardPropsById$ === void 0 ? void 0 : _actionCardPropsById$.activeBranchIndex) === branchIndex;
  }, [actionCardPropsById]);
  const onRenderEndMarker = useCallback(({
    edgeId
  }) => {
    const prevActionId = getPreviousActionId(edgeId);
    const branchIndex = getBranchIndex(edgeId);
    const prevActionCardProps = actionCardPropsById === null || actionCardPropsById === void 0 ? void 0 : actionCardPropsById.get(prevActionId);
    const borderStyle = !isEdgeOfBranchAction(flow, edgeId) || (prevActionCardProps === null || prevActionCardProps === void 0 ? void 0 : prevActionCardProps.activeBranchIndex) === branchIndex ? prevActionCardProps === null || prevActionCardProps === void 0 ? void 0 : prevActionCardProps.borderStyle : undefined;
    return /*#__PURE__*/_jsx(EndMarker, {
      disabled: prevActionCardProps === null || prevActionCardProps === void 0 ? void 0 : prevActionCardProps.disabled,
      flow: flow,
      edgeId: edgeId,
      hideFooter: true,
      borderStyle: borderStyle,
      zoomLevel: zoomLevel
    });
  }, [actionCardPropsById, flow, zoomLevel]);
  const onRenderBranchName = useCallback(edgeId => {
    const prevActionId = getPreviousActionId(edgeId);
    const prevActionCardProps = actionCardPropsById === null || actionCardPropsById === void 0 ? void 0 : actionCardPropsById.get(prevActionId);
    const isHighlighted = getIsBranchEdgeHighlighted(edgeId);
    const borderStyle = isHighlighted ? prevActionCardProps === null || prevActionCardProps === void 0 ? void 0 : prevActionCardProps.borderStyle : undefined;
    return /*#__PURE__*/_jsx(BranchLabel, {
      disabled: prevActionCardProps === null || prevActionCardProps === void 0 ? void 0 : prevActionCardProps.disabled,
      zoomLevel: zoomLevel,
      borderStyle: borderStyle,
      branchLabelHeaderText: /*#__PURE__*/_jsx(BranchName, {
        flow: flow,
        edgeId: edgeId,
        handleGetActionOutputsForTargetActionById: handleGetActionOutputsForTargetActionById
      }),
      branchDescription: /*#__PURE__*/_jsx(BranchDescription, {
        flow: flow,
        edgeId: edgeId
      })
    });
  }, [flow, handleGetActionOutputsForTargetActionById, zoomLevel, actionCardPropsById, getIsBranchEdgeHighlighted]);
  return /*#__PURE__*/_jsx(FlowCanvasBase, Object.assign({}, rest, {
    flow: flow,
    flowId: flow.flowId,
    mode: "flowView",
    canvasScrollElementRef: canvasScrollElementRef,
    onSelectAction: onSelectNode,
    ComposedCanvas: ComposedCanvas,
    readOnlyOverride: true,
    centerOnMount: true,
    onRenderEndMarker: onRenderEndMarker,
    onRenderCanvasNode: onRenderCanvasNode,
    highlightedEdgeThickness: 4,
    highlightedEdgeColor: OZ_DARK,
    highlightedActionIds: highlightedActionIds,
    getIsBranchEdgeHighlighted: getIsBranchEdgeHighlighted,
    onRenderBranchName: onRenderBranchName,
    zoomLevel: zoomLevel,
    goToEditNodeId: goToEditNodeId
  }));
};
const ConnectedFlowViewSimpleCanvas = compose(wireAuthConsumer, withRouter, LazyLangLoaderDecorator([actionFeaturesTranslations]))(FlowViewSimpleCanvas);
export default ConnectedFlowViewSimpleCanvas;